// src/components/sat/CalculatorPanel.tsx
'use client'

import { useState } from 'react'
import { Button } from '@/components/ui/button'
import DesmosCalc from './DesmosCalc'

/**
 * Inline Desmos calculator with a toggle, for the non-test Math surfaces
 * (practice, drills, review cards). Mirrors the SAT Runner's calculator
 * affordance so practice matches the real test: a default-closed "Calculator"
 * button reveals the embedded graphing calculator — the same one the digital
 * SAT shows in Bluebook — alongside the CAS-policy disclaimer and an
 * open-in-new-tab fallback.
 *
 * Self-contained: owns its open/closed state and reuses the shared, propless
 * <DesmosCalc />, whose ~4MB script loads only when first opened and via a
 * cross-mount singleton (so several panels on a page never double-load).
 *
 * Gate it at the call site on the question being Math — render
 * `{isMath && <CalculatorPanel />}` so it never appears on Reading & Writing.
 */
export default function CalculatorPanel({ className }: { className?: string }) {
  const [open, setOpen] = useState(false)

  return (
    <div className={className}>
      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          variant="secondary"
          size="sm"
          className="min-h-[44px]"
          onClick={() => setOpen((o) => !o)}
          aria-label={open ? 'Hide calculator' : 'Show calculator'}
          aria-expanded={open}
        >
          {open ? 'Hide calc' : 'Calculator'}
        </Button>
        <a
          href="https://www.desmos.com/calculator?lang=en"
          target="_blank"
          rel="noopener noreferrer"
          className="hidden text-sm text-muted-foreground underline sm:inline"
        >
          open in new tab
        </a>
      </div>

      {open && (
        <div className="mt-3 w-full">
          <DesmosCalc />
          <div className="mt-2 text-xs text-muted-foreground">
            Built-in graphing calculator — the same one the digital SAT provides. Handheld
            calculators with CAS are <b>not permitted</b> on the SAT.
          </div>
        </div>
      )}
    </div>
  )
}
